"use client";

import React from "react";
import { Mail, ArrowRight, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-2xl bg-primary/5 p-8 text-center border border-primary/10">
        <div className="rounded-lg bg-primary/10 p-3">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-xl font-semibold text-foreground">
            با موفقیت ثبت شد!
          </h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md">
          ثبت ایمیل دیگر
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-5 text-right" dir="rtl">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">نام شما</FormLabel>
              <FormControl>
                <Input placeholder="مثلاً: مریم" className="bg-background border-border focus:ring-primary rounded-md" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "وارد کردن ایمیل الزامی است",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "لطفاً یک ایمیل معتبر وارد کنید"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">
                آدرس ایمیل <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="example@mail.com" className="bg-background border-border focus:ring-primary rounded-md" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription className="text-xs font-default">
                ایمیل شما برای ارسال مطالب هفتگی استفاده می‌شود
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consent" rules={{
      required: "پذیرش قوانین برای ادامه الزامی است"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-border bg-background/50 p-4 shadow-sm">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-sm border-primary text-primary focus:ring-primary accent-primary" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} />
            </FormControl>
            <div className="space-y-1 leading-none mr-3">
              <FormLabel className="text-sm font-medium text-foreground cursor-pointer font-default">
                موافقم که مطالب آموزشی و خبرنامه را دریافت کنم <span className="text-destructive">*</span>
              </FormLabel>
              <FormDescription className="text-xs text-muted-foreground font-default">
                با تیک زدن این گزینه، عضویت خود را تایید می‌کنید
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20 font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto sm:min-w-[200px] h-12 text-base font-medium shadow-md rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity" size="lg">
        {isSubmitting ? <>
            <Loader className="ml-2 size-5 animate-spin" />
            در حال پردازش...
          </> : <>
            عضویت در خبرنامه
            <ArrowRight className="mr-2 size-5 rotate-180" />
          </>}
      </Button>
    </Form>;
}
export default function NewsletterDeOfertas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="224896" id="اشتراک-در-خبرنامه" className="relative w-full overflow-hidden bg-background text-foreground py-24">
      {}
      <div className="absolute inset-0 pointer-events-none flex justify-center items-center overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-secondary/10 blur-[120px] rounded-lg" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div ref={ref} className={`mx-auto max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col items-center text-center space-y-8 rounded-3xl border border-border/40 bg-card/40 p-8 md:p-12 shadow-lg backdrop-blur-md">
            
            <div className="space-y-4 max-w-2xl">
              <div className="inline-flex items-center rounded-lg border border-secondary/20 bg-secondary/10 px-4 py-1.5 text-sm font-medium text-secondary">
                <Mail className="ml-2 size-4" />
                جامعه سلامت روان
              </div>
              <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
                دریافت هفتگی <span className="text-secondary">دانستنی‌های روانشناسی</span>
              </h2>
              <p className="text-lg text-muted-foreground font-default leading-relaxed">
                به جامعه ما بپیوندید تا هر هفته جدیدترین مقالات و راهکارهای دکتر مهرنوش اسماعیلی در زمینه سلامت روان، مدیریت اضطراب و رشد فردی را مستقیماً در ایمیل خود دریافت کنید.
              </p>
            </div>

            <div className="w-full max-w-xl mx-auto">
              <FormProvider formKey="newsletter_form_insights" formId="newsletter_form_insights" sectionName="اشتراک-در-خبرنامه" defaultValues={{
              email: "",
              first_name: "",
              consent: false
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              },
              consent: {
                required: true
              }
            }} submitText="عضویت در خبرنامه" successMessage="سپاس! شما با موفقیت به لیست خبرنامه دکتر اسماعیلی اضافه شدید." errorMessage="متأسفانه خطایی رخ داد. لطفاً دوباره تلاش کنید.">
                <NewsletterFormInner />
              </FormProvider>
            </div>
            
            <p className="text-xs text-muted-foreground max-w-md mx-auto font-default">
              با عضویت، شما با شرایط خدمات و سیاست حریم خصوصی ما موافقت می‌کنید. ما به حریم خصوصی شما احترام می‌گذاریم و اسپم نمی‌فرستیم.
            </p>
          </div>
        </div>
      </div>
    </section>;
}